import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import { EmployeeCell, truncatableCell } from "../Shared/tableUi";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  Add,
  AddCircleOutline,
  AccessTimeOutlined,
  CurrencyRupee,
  DeleteOutline,
  EventBusyOutlined,
  EventRepeatOutlined,
  FileUploadOutlined,
  KeyboardArrowDown,
  LayersOutlined,
  CheckCircleOutline,
  CancelOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection, scrollTop } from "services/constants";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { penaltyHeader } from "pages/PayRoll/PayRollHeaders";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
  APP_BAR_HEIGHT,
} from "services/constants/designTokens";
import { API_DATE, emptyForm, rowToForm } from "./penaltiesData";
import { listPenalties, savePenalty, deletePenalty, bulkPenalties } from "./penaltiesApi";
import { PenaltyFormPanel, PenaltyViewPanel } from "./PenaltyPanels";
import { PenaltyImport } from "./PenaltyImport";
import { ChangePayrollCycle } from "../ShiftEarnings/ChangePayrollCycle";
import { BulkSelectionBar } from "../Shared/BulkSelectionBar";
import { SourceTabs, filterBySourceTab } from "../Shared/SourceTabs";
import { usePayInputConfig, isImportAllowed, isSourceAllowed } from "../Shared/usePayInputConfig";
import { Redirect } from "react-router-dom";
import { formatDate, formatExactQty, formatMoney } from "../Shared/formatters";
import { bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import {
  ColGroup,
  ProxyScrollbar,
  StickyHeadStrip,
  fixedTableSx,
  headScrollCellSx,
  hiddenHeadCellSx,
  pinnedCellSx,
  pinnedColumns,
  scrollBodySx,
  timelineScopeSx,
  useStickyHorizontalScroll,
} from "../Shared/stickyScroll";
import { useQuickActionFromUrl } from "components/GlobalSearch/useQuickActionFromUrl";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Pay Inputs > Penalties: the Shift Earnings list for
// TblPayrollDailyPenalty rows (Late Entry, Early Exit, Break Penalty,
// Absent), on the Penalty APIs under payroll/deduction/. Rows come from the
// attendance calculations (Attendance), Add (Manual) and Import.
const SUMMARY_CARDS = [
  { key: "records", title: "Penalty Records", icon: LayersOutlined, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "lateEarly", title: "Late / Early Exit", icon: AccessTimeOutlined, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "breakAbsent", title: "Break / Absent", icon: EventBusyOutlined, accent: "#DC2626", tint: "#FEF2F2" },
  { key: "total", title: "Total Penalty", icon: CurrencyRupee, accent: "#F0954A", tint: "#FFF4E5" },
];


const TONES = {
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
  amber: { bg: "#FFF4E5", fg: "#B54708" },
  red: { bg: "#FEF2F2", fg: "#B42318" },
  purple: { bg: "#F1EDFF", fg: "#5B4BD5" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
};

const PENALTY_TYPE_TONES = {
  "Late Entry": "amber",
  "Early Exit": "purple",
  "Break Penalty": "blue",
  Absent: "red",
};

const SOURCE_TONES = { Attendance: "blue", Manual: "purple", Import: "amber" };

const NUMERIC_KEYS = new Set([
  "quantity",
  "rate",
  "payment_factor",
  "payable_amount",
]);

// Types come from payroll/deduction/penalty/types/, so the cards group them
// by name: anything with "late" or "early" in it vs everything else.
const isLateEarly = (type) => /late|early/i.test(String(type ?? ""));

// Fixed column widths (px): header and body are separate tables
// (Shared/stickyScroll.js) and must share widths to line up.
const COLUMN_WIDTHS = {
  date: 120,
  employee: 200,
  department: 160,
  penalty_type: 130,
  shift_name: 150,
  basis: 100,
  quantity: 120,
  rate: 120,
  payment_factor: 90,
  payable_amount: 130,
  payroll_cycle: 130,
  source: 120,
  status: 130,
};

const PINNED_KEYS = new Set(["employee", "department"]);
// Employee, Department, Date always lead the list in every saved view and
// can't be dragged to another place in the view editor.
const FIXED_COLUMNS = ["employee", "department", "date"];

const sum = (rows, key) =>
  Math.round(rows.reduce((total, row) => total + (Number(row[key]) || 0), 0) * 100) / 100;

const ToneChip = ({ label, tone }) => {
  if (!label) return "–";
  const colors = TONES[tone] || TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{
        ...designType.captionText,
        fontWeight: designWeights.medium,
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: designRadii.compactButton,
        backgroundColor: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Mui.Box>
  );
};


// Rows with no payroll cycle all count as one ("Unassigned") cycle.
const cycleKey = (row) => String(row.pay_schedule_id ?? "");

// Teal row checkboxes, shared with the Approvals bulk selection.
const checkboxSx = bulkSelectionCheckboxSx;

const thisMonth = () => ({
  start: moment().startOf("month").format(API_DATE),
  end: moment().endOf("month").format(API_DATE),
  label: "This Month",
});

export const Penalties = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  return <PenaltiesScreen key={String(domain) + ":" + String(topbarCompanyId)} {...props} />;
};

const PenaltiesScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const canEdit = checkEditorPermission(
    userScreenAccess?.module_details,
    "Deductions",
    "Penalties",
  );

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mutationRef = useRef(null);
  const validCompany = Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  // Payroll > Configuration > Pay Input > Penalties: Enable Penalties turns
  // this screen off, Approval Required shows / hides the approval status and
  // actions, and Allowed Sources decide the Add menu. If the settings can't
  // be loaded, everything stays on.
  const { config: payInputConfig, loading: configLoading } = usePayInputConfig(domain, topbarCompanyId, "PENALTY", validCompany);
  const configEnabled = payInputConfig?.enabled !== false;
  const approvalRequired = !payInputConfig || payInputConfig.approvalRequired === "yes";
  const manualAllowed = isSourceAllowed(payInputConfig, "MANUAL");
  const importAllowed = isImportAllowed(payInputConfig);
  const [period, setPeriod] = useState(thisMonth);
  // All Records / From Attendance / Imported / Manual Entries.
  const [sourceTab, setSourceTab] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Panels, menus, dialogs.
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [formPanel, setFormPanel] = useState({ open: false, mode: "add", rowId: null, initial: null });
  const [viewRowId, setViewRowId] = useState(null);
  const [deleteIds, setDeleteIds] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [cycleWizardOpen, setCycleWizardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setAllRows([]); setSelectedIds(new Set()); setPage(0);
    if (!validCompany || !domain) { setLoading(false); return () => controller.abort(); }
    setLoading(true);
    listPenalties(domain, topbarCompanyId, period, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setAllRows(rows); })
      // Errors show as a toast, not a card on the list; the table stays empty.
      .catch((error) => { if (!controller.signal.aborted) showToast("error", error.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, period, validCompany, refresh]);

  useEffect(() => () => mutationRef.current?.abort(), []);

  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true; setBusy(true);
    const controller = new AbortController(); mutationRef.current = controller;
    try {
      const value = await action(controller.signal);
      if (controller.signal.aborted) return { success: false };
      return { success: true, value };
    } catch (error) {
      if (!controller.signal.aborted) showToast("error", error.message);
      return { success: false };
    } finally {
      if (!controller.signal.aborted) { busyRef.current = false; setBusy(false); }
    }
  };

  const tableContainerRef = useRef(null);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  // The sticky table header sits under the CustomFilter bar, whose height
  // changes once filter chips appear — measured, same as Users.
  useEffect(() => {
    if (!customFilterNode) return undefined;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const update = () => setFooterHeight(footerNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  const handleSavedViewChange = useCallback(() => setPage(0), []);

  // "Default View ▾" selector, same as the other redesigned lists.
  const { visibleHeaders: savedViewHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "deduction_penalty_saved_views",
    fullHeaders: penaltyHeader,
    screenAccess: canEdit,
    onViewChange: handleSavedViewChange,
    fixedLeadingKeys: FIXED_COLUMNS,
  });
  // No approval step configured: the Status column is not shown.
  const visibleHeaders = useMemo(
    () => (approvalRequired ? savedViewHeaders : savedViewHeaders?.filter((head) => head.key !== "status")),
    [approvalRequired, savedViewHeaders],
  );

  // ---- Data pipeline: period -> source tab -> CustomFilter ----

  const periodAllRows = useMemo(
    () =>
      allRows
        .filter((row) => row.date >= period.start && row.date <= period.end)
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .map((row) => ({
          ...row,
          employee: `${row.employee_name} (${row.employee_code})`,
          department: row.department_name || row.department || "",
        })),
    [allRows, period],
  );

  // The selected source tab's rows: the cards, table and filters use these.
  const periodRows = useMemo(() => filterBySourceTab(periodAllRows, sourceTab), [periodAllRows, sourceTab]);

  // CustomFilter reports its result one render after periodRows changes;
  // only trust it when it was computed from the current periodRows (Users
  // pattern).
  const [filteredRows, setFilteredRows] = useState(null);
  const filteredSourceRef = useRef(null);
  const visibleRows =
    filteredRows !== null && filteredSourceRef.current === periodRows
      ? filteredRows
      : periodRows;

  const handleFilteredDataChange = useCallback(
    (data) => {
      filteredSourceRef.current = periodRows;
      setFilteredRows(data);
      if (page * rowsPerPage >= data.length) setPage(0);
    },
    [page, rowsPerPage, periodRows],
  );

  const summaryValues = useMemo(
    () => ({
      records: visibleRows.length.toLocaleString("en-IN"),
      lateEarly: formatMoney(sum(visibleRows.filter((row) => isLateEarly(row.penalty_type)), "payable_amount")),
      breakAbsent: formatMoney(sum(visibleRows.filter((row) => !isLateEarly(row.penalty_type)), "payable_amount")),
      total: formatMoney(sum(visibleRows, "payable_amount")),
    }),
    [visibleRows],
  );

  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  // Ticked rows must share one payroll cycle and one approval status: the
  // first ticked row sets both, and other rows can't be ticked until the
  // selection is cleared. So Approved records are never mixed with Pending /
  // Rejected ones in a bulk action. Without an approval step (status hidden)
  // only the payroll cycle counts.
  const selectedCycleRow = visibleRows.find((row) => selectedIds.has(row.id));
  const selectedCycle = selectedCycleRow ? cycleKey(selectedCycleRow) : null;
  const sameGroup = (row, anchor) =>
    cycleKey(row) === cycleKey(anchor) && (!approvalRequired || (row.status || "") === (anchor.status || ""));
  const isSelectable = (row) => !selectedCycleRow || sameGroup(row, selectedCycleRow);
  const pageAnchor = selectedCycleRow ?? pageRows[0] ?? null;
  const selectablePageRows = pageAnchor ? pageRows.filter((row) => sameGroup(row, pageAnchor)) : [];
  // Hover text on a greyed-out checkbox.
  const blockedReason = (row) => {
    if (!selectedCycleRow || isSelectable(row)) return "";
    if (cycleKey(row) !== selectedCycle) return `Only records in the same payroll cycle (${selectedCycleRow.payroll_cycle}) can be selected together.`;
    return `Only ${selectedCycleRow.status || "same-status"} records can be selected together. Clear the selection to select ${row.status || "other"} records.`;
  };
  const pageSelectedCount = pageRows.filter((row) => selectedIds.has(row.id)).length;
  const allPageSelected = selectablePageRows.length > 0 && selectablePageRows.every((row) => selectedIds.has(row.id));

  // Selection only ever covers rows that are still listed.
  useEffect(() => {
    setSelectedIds((current) => {
      const listed = new Set(visibleRows.map((row) => row.id));
      const next = new Set([...current].filter((id) => listed.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [visibleRows]);

  const togglePageSelection = () =>
    setSelectedIds((current) => {
      const next = new Set(current);
      selectablePageRows.forEach((row) =>
        allPageSelected ? next.delete(row.id) : next.add(row.id),
      );
      return next;
    });

  const toggleRowSelection = (row) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(row.id)) next.delete(row.id);
      else if (isSelectable(row)) next.add(row.id);
      return next;
    });

  const handleFormSave = async (form) => {
    const result = await runAction((signal) => savePenalty(domain, form, formPanel.mode === "edit" ? formPanel.rowId : null, signal, formPanel.mode === "edit" ? formPanel.initial : null));
    if (!result.success) return;
    setAllRows((rows) => [result.value, ...rows.filter((row) => row.id !== result.value.id)]);
    setFormPanel((current) => ({ ...current, open: false }));
    showToast("success", "Penalty saved.");
  };

  const handleDelete = async () => {
    const ids = [...deleteIds];
    const outcome = await runAction(async (signal) => {
      const results = await Promise.allSettled(ids.map((id) => deletePenalty(domain, id, signal)));
      return { deleted: ids.filter((_, i) => results[i].status === "fulfilled"), errors: results.flatMap((r, i) => r.status === "rejected" ? ["Record " + ids[i] + ": " + r.reason.message] : []) };
    });
    if (!outcome.success) return;
    const { deleted, errors } = outcome.value;
    setAllRows((rows) => rows.filter((row) => !deleted.includes(row.id)));
    if (deleted.includes(viewRowId)) setViewRowId(null);
    setDeleteIds(null);
    if (deleted.length) showToast("success", deleted.length + " deleted.");
    if (errors.length) showToast("error", errors.join(" "));
  };
  const selected = [...selectedIds];
  // When every ticked row already has one status, the action that sets that
  // status would change nothing, so it is left out of the Actions menu
  // (same as Shift Earnings / Overtime).
  const selectedStatuses = new Set(allRows.filter((row) => selectedIds.has(row.id)).map((row) => row.status));
  const sharedStatus = selectedStatuses.size === 1 ? [...selectedStatuses][0] : null;
  const guard = (action) => {
    if (busy || loading || !validCompany) return;
    if (!canEdit) { showToast("error", "Access denied: You are not authorized to change penalties"); return; }
    action();
  };
  const applyBulk = async (ids, changes) => {
    const result = await runAction((signal) => bulkPenalties(domain, ids, changes, signal));
    if (!result.success) return null;
    const { updated = [], errors = [] } = result.value;
    if (updated.length) { setRefresh((n) => n + 1); showToast("success", updated.length + " record(s) updated."); }
    if (errors.length) showToast("error", errors.map((error) => "Record " + error.id + ": " + error.message).join(" "));
    return { updated: updated.length, skipped: ids.length - updated.length, errors };
  };
  const handleChangeCycle = async (ids, cycle) => {
    const result = await applyBulk(ids, { pay_schedule_id: Number(cycle) });
    if (!result) throw new Error("Payroll cycle could not be updated. Please try again.");
    return result;
  };
  const handleStatus = async () => {
    const result = await applyBulk(statusAction.ids, { status: statusAction.status });
    if (result) setStatusAction(null);
  };

  const openEdit = (row) =>
    setFormPanel({
      open: true,
      mode: "edit",
      rowId: row.id,
      source: row.source,
      initial: { ...rowToForm(row), payroll_cycle: row.payroll_cycle },
    });

  const viewRow = allRows.find((row) => row.id === viewRowId) ?? null;

  // View and Add/Edit share one expand state: Edit opened from an expanded
  // view opens expanded too (and the view is still expanded behind it). It
  // goes back to collapsed once both panels are closed.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = () => setPanelExpanded((value) => !value);
  const anyPanelOpen = Boolean(viewRow) || formPanel.open;
  useEffect(() => {
    if (!anyPanelOpen) setPanelExpanded(false);
  }, [anyPanelOpen]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(period.start).format("DD/MM/YYYY")} to ${moment(period.end).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [period, topbarCompanyName],
  );

  const renderCell = (row, key) => {
    switch (key) {
      case "date":
        return formatDate(row.date);
      case "employee":
        return <EmployeeCell name={row.employee_name} code={row.employee_code} />;
      case "penalty_type":
        return <ToneChip label={row.penalty_type} tone={PENALTY_TYPE_TONES[row.penalty_type]} />;
      case "source":
        return <ToneChip label={row.source} tone={SOURCE_TONES[row.source]} />;
      // Shared status chip (Approved green, Pending amber, Rejected red).
      case "status":
        return row.status ? <RequestStatusChip status={row.status} /> : "–";
      case "payable_amount":
        return row.payable_amount == null ? "–" : formatMoney(row.payable_amount);
      case "rate":
        return row.rate == null ? "–" : formatMoney(row.rate);
      // Quantity is shifts ("0.5"), hours ("00:30") or, for amount basis,
      // the amount - shown as sent.
      case "quantity":
        return row.quantity === "" || row.quantity == null ? "–" : String(row.quantity);
      case "payment_factor":
        return formatExactQty(row[key]);
      default:
        return row[key] || "–";
    }
  };

  // The header strip itself is sticky, so its cells are not.
  const headCellSx = {
    ...sharedTableHeadCellSx,
    position: "static",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const colWidths = [52, ...(visibleHeaders ?? []).map((head) => COLUMN_WIDTHS[head.key] ?? 130)];
  // Checkbox, Employee and Department stay pinned on the left while the rest
  // scrolls sideways (column 0 is the checkbox).
  const pinned = pinnedColumns(colWidths, [
    0,
    ...(visibleHeaders ?? []).flatMap((head, index) => (PINNED_KEYS.has(head.key) ? [index + 1] : [])),
  ]);
  const pinSx = (index, head = false) =>
    index in pinned.lefts
      ? pinnedCellSx({ left: pinned.lefts[index], last: index === pinned.last, head })
      : head
        ? headScrollCellSx
        : {};
  const { headRef, bodyRef, proxyRef, proxyInnerRef } = useStickyHorizontalScroll([
    colWidths.join(","),
    pageRows.length,
    loading,
    footerHeight,
  ]);

  const addMenuItems = [
    ...(manualAllowed
      ? [{ label: "Add Penalty", icon: AddCircleOutline, onClick: () => setFormPanel({ open: true, mode: "add", rowId: null, initial: emptyForm() }) }]
      : []),
    ...(importAllowed
      ? [{ label: "Import Penalties", icon: FileUploadOutlined, onClick: () => setImportOpen(true) }]
      : []),
  ];
  // Opened from a search quick action ("Add …"): open that Add drawer.
  useQuickActionFromUrl(addMenuItems, !loading && !configLoading);

  // Turned off in Pay Input Configuration: the sidebar hides this screen, so
  // an old link or bookmark goes to the next Pay Inputs screen instead.
  // Nothing is drawn until the setting is known, so the list never flashes.
  if (configLoading) return <Loader info="Loading..." minHeight={`calc(100vh - ${APP_BAR_HEIGHT}px)`} />;
  if (!configEnabled) return <Redirect to="/admin/payroll/pay-inputs/damage-and-recovery" />;

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        {/* Header */}
        <Mui.Box
          sx={{
            ...designShell.headerRowBase,
            alignItems: { xs: "flex-start", sm: "center" },
            px: 2,
            pt: 2,
            mb: 2,
          }}
        >
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Pay Inputs", "Penalties"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Penalties
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Manage penalties deducted in payroll. View, add, import or
              edit late entry, early exit, break and absent penalties.
            </Mui.Typography>
          </Mui.Box>
          {/* Period next to the Add button, same as the Leave / Team
              Workspace Approvals header (date range + New Request). */}
          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <ApprovalDateRangeFilter
              startDate={period.start}
              endDate={period.end}
              dateRangeLabel={period.label}
              onChange={(start, end, label) => {
                setPage(0);
                setPeriod({
                  start: moment(start).format(API_DATE),
                  end: moment(end).format(API_DATE),
                  label,
                });
              }}
              alwaysShowCalendars
              // Same height as the Add button next to it (primaryButtonBase).
              sx={{ minHeight: 46, height: 46 }}
            />
            {canEdit && addMenuItems.length ? (
              <Mui.Button
                disabled={busy || loading || !validCompany}
                startIcon={<Add />}
                endIcon={<KeyboardArrowDown />}
                onClick={(event) => setAddMenuAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(addMenuAnchor)}
                sx={designShell.primaryButtonBase}
              >
                Add Penalty
              </Mui.Button>
            ) : null}
            <OpenSettingsButton group="pay-input" item="penalties" />
          </Mui.Stack>
        </Mui.Box>

        <Mui.Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {addMenuItems.map((item) => (
            <Mui.MenuItem
              key={item.label}
              disabled={item.disabled}
              sx={designShell.actionMenuItem}
              onClick={() => {
                setAddMenuAnchor(null);
                item.onClick();
              }}
            >
              <Mui.ListItemIcon>
                <item.icon fontSize="small" sx={{ color: designColors.tealDark }} />
              </Mui.ListItemIcon>
              {item.label}
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2 }}>
          {!validCompany ? <Mui.Alert severity="info" sx={{ mb: 2 }}>Select a single company to view penalties.</Mui.Alert> : null}
          {/* Source tabs, above the cards: the cards follow the selected tab. */}
          <SourceTabs
            tabs={["all", "attendance", "imported", "manual"]}
            value={sourceTab}
            rows={periodAllRows}
            onChange={(tab) => {
              setSourceTab(tab);
              setPage(0);
            }}
          />

          {/* Summary */}
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {SUMMARY_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={summaryValues[card.key]}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          {/* Toolbar */}
          <Mui.Box
            ref={customFilterRef}
            sx={{
              position: "sticky",
              top: APP_BAR_OFFSET,
              zIndex: 4,
              backgroundColor: designColors.white,
            }}
          >
            <CustomFilter
              title="Penalties"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={periodRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search..."
            />
          </Mui.Box>

          {/* Selection bar (Approvals pattern), only while rows are ticked */}
          <BulkSelectionBar
            selectedCount={selected.length}
            onClear={() => setSelectedIds(new Set())}
            actions={[
              { label: "Change Payroll Cycle", icon: EventRepeatOutlined, onClick: () => guard(() => setCycleWizardOpen(true)) },
              // Approve / Reject only when the configuration has an approval step.
              ...(approvalRequired
                ? [
                { label: "Approve", status: "Approved", icon: CheckCircleOutline },
                { label: "Reject", status: "Rejected", icon: CancelOutlined },
              ]
                .filter((action) => action.status !== sharedStatus)
                .map(({ status, ...action }) => ({ ...action, onClick: () => guard(() => setStatusAction({ ids: selected, status })) }))
                : []),
              { label: "Delete", icon: DeleteOutline, onClick: () => guard(() => setDeleteIds(selected)), danger: true },
            ]}
          />

          {/* Table: scrolls sideways inside the card (Monthly roster
              pattern, Shared/stickyScroll.js) with the header stuck under
              the toolbar and a sticky scrollbar above the footer. */}
          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0, ...timelineScopeSx }}>
            <StickyHeadStrip headRef={headRef} top={APP_BAR_OFFSET + customFilterHeight} colWidths={colWidths}>
              <Mui.TableHead>
                <Mui.TableRow>
                  <Mui.TableCell padding="checkbox" sx={{ ...headCellSx, ...pinSx(0, true) }}>
                    <Mui.Checkbox
                      size="small"
                      sx={checkboxSx}
                      checked={allPageSelected}
                      indeterminate={pageSelectedCount > 0 && !allPageSelected}
                      onChange={togglePageSelection}
                      inputProps={{ "aria-label": "Select all rows on this page" }}
                    />
                  </Mui.TableCell>
                  {visibleHeaders?.map((head, index) => (
                    <Mui.TableCell
                      key={head.key}
                      align={NUMERIC_KEYS.has(head.key) ? "right" : "left"}
                      sx={{ ...headCellSx, ...pinSx(index + 1, true) }}
                    >
                      <TruncatedTableValue value={head.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
            </StickyHeadStrip>
            <Mui.Box
              ref={(node) => {
                bodyRef.current = node;
                tableContainerRef.current = node;
              }}
              sx={{ ...scrollBodySx, pb: footerHeight ? `${footerHeight}px` : 0 }}
            >
              <ApproveTable sx={fixedTableSx(colWidths)}>
                <ColGroup colWidths={colWidths} />
                {/* Zero-height header copy: phone cards are labelled from it. */}
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell padding="checkbox" sx={hiddenHeadCellSx} />
                    {visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} sx={hiddenHeadCellSx}>
                        <Mui.Box component="span">{head.name}</Mui.Box>
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={(visibleHeaders?.length ?? 0) + 1} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState
                      dataLength={periodRows}
                      colSpan={(visibleHeaders?.length ?? 0) + 1}
                    />
                  ) : (
                    pageRows.map((row) => {
                      const isSelected = selectedIds.has(row.id);
                      return (
                        <Mui.TableRow
                          key={row.id}
                          hover
                          onClick={() => setViewRowId(row.id)}
                          // A ticked row shows only its ticked checkbox:
                          // no tint or accent line on the row itself.
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          <Mui.TableCell
                            padding="checkbox"
                            sx={{ ...sharedTableBodyCellSx, ...pinSx(0) }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Mui.Tooltip
                              arrow
                              placement="top"
                              slotProps={tooltipStyleProps}
                              title={isSelected ? "" : blockedReason(row)}
                            >
                              <Mui.Box component="span">
                                <Mui.Checkbox
                                  size="small"
                                  sx={checkboxSx}
                                  checked={isSelected}
                                  disabled={!isSelected && !isSelectable(row)}
                                  onChange={() => toggleRowSelection(row)}
                                  inputProps={{ "aria-label": `Select ${row.employee_name}` }}
                                />
                              </Mui.Box>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          {visibleHeaders.map((head, index) => (
                            <Mui.TableCell
                              key={head.key}
                              align={NUMERIC_KEYS.has(head.key) ? "right" : "left"}
                              sx={{ ...sharedTableBodyCellSx, ...pinSx(index + 1) }}
                            >
                              {truncatableCell(renderCell(row, head.key))}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      );
                    })
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.Box>
            <ProxyScrollbar
              proxyRef={proxyRef}
              proxyInnerRef={proxyInnerRef}
              bottom={Math.max(0, footerHeight - 1)}
            />

            {/* Footer: the standard pagination footer, same as the other
                tables. Ticked rows (selectedIds) get their actions later. */}
            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <PenaltyViewPanel
        open={Boolean(viewRow)}
        row={viewRow}
        canEdit={canEdit}
        showApproval={approvalRequired}
        domain={domain}
        busy={busy}
        // Only while open: GlobalSidePanel keeps showing an expanded panel
        // even after `open` turns false.
        expanded={Boolean(viewRow) && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setViewRowId(null)}
        onEdit={openEdit}
        onDelete={(row) => setDeleteIds([row.id])}
        // Approve / Reject from the view: same confirm + bulk call as the
        // list's tick-box action, for this one entry.
        onStatus={(row, status) => guard(() => setStatusAction({ ids: [row.id], status }))}
      />

      <PenaltyFormPanel
        open={formPanel.open}
        mode={formPanel.mode}
        initial={formPanel.initial}
        source={formPanel.source}
        saving={busy}
        expanded={formPanel.open && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => { if (!busyRef.current) setFormPanel((current) => ({ ...current, open: false })); }}
        onSave={handleFormSave}
      />

      <PenaltyImport
        domain={domain}
        companyId={topbarCompanyId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        // Same toasts as the Leave / Overtime imports: the server's per-row result.
        onImported={(result) => {
          const success = Number(result.success) || 0;
          if (success > 0) {
            setRefresh((n) => n + 1);
            showToast("success", `${success} penalt${success === 1 ? "y" : "ies"} imported.`);
          }
          if (Number(result.failed) > 0) showToast("error", `${result.failed} record(s) could not be imported. See the import result for details.`);
        }}
      />

      <ChangePayrollCycle
        domain={domain}
        recordLabel="penalty"
        recordsLabel="penalties"
        screenLabel="Penalties"
        typeColumn={{ key: "penalty_type", label: "Penalty Type" }}
        open={cycleWizardOpen}
        rows={periodAllRows}
        selectedIds={selected}
        onClose={() => setCycleWizardOpen(false)}
        onApply={handleChangeCycle}
      />

      <GlobalConfirmDialog
        open={Boolean(statusAction)}
        title={statusAction?.status === "Approved" ? "Approve penalties" : "Reject penalties"}
        onCancel={() => { if (!busyRef.current) setStatusAction(null); }}
        onConfirm={handleStatus}
        confirmLabel={busy ? "Saving..." : "Confirm"}
        cancelLabel="Cancel"
      >
        Update the approval status of {statusAction?.ids.length ?? 0} selected record(s) to {statusAction?.status}?
      </GlobalConfirmDialog>

      <GlobalConfirmDialog
        open={Boolean(deleteIds)}
        title="Delete penalties"
        onCancel={() => { if (!busyRef.current) setDeleteIds(null); }}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
      >
        Delete {deleteIds?.length ?? 0}{" "}
        {deleteIds?.length === 1 ? "entry" : "entries"}? This cannot be undone.
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
